import type { BranchId } from "~/branching/model/branch";

export const routes = {
  Root: "/",
  SignIn: "/sign-in",
  Systems: "/systems",
  SystemsDashboard: "/systems-dashboard",
  AiChat: "/ai-chat",
  SystemHome: "/systems-dashboard/:systemId",
  SystemHomeDirectory: "directory/:nodeId?",
  SystemHomeWorkflows: "workflows",
  SystemHomeBranches: "branches",
  SystemHomeSettings: "settings",
  SystemHomeCommits: "commits",
  SystemHomeChangeRequests: "change-requests",
  SystemHomeChangeRequestDetail: "change-requests/:id",
  SystemHomeCreateChangeRequest: "change-requests/new",
  SystemHomeBranchScope: "bid/:branchId",
  Resources: "/resources",
  Libraries: "/libraries",
  LibraryDetails: "/libraries/:libraryId",
  PackageDetails: "/libraries/:libraryId/packages/:packageId",
  Package: "/packages/:packageId",
  ResourcesDashboard: "/resources-dashboard",
  Jobs: "/jobs",
  JobDetails: "/jobs/:jobId",
  Admin: "/admin",
  Settings: "/settings",
  SettingsProfile: "/settings/profile",
  SettingsPassword: "/settings/password",
  SettingsNotifications: "/settings/notifications",
  SettingsApplication: "/settings/application",
  SettingsDeveloper: "/settings/developer",
  SettingsAiInstructions: "/settings/ai-instructions",
  SettingsLinkedAccounts: "/settings/linked-accounts",
  Comment: "/comment",
  FunctionAuthCallback: "/callback",
  NotFound: "/404",
  Documentation: "/documentation",
  MagicDocs: "/magic-docs",
  Upload: "/upload",
  AdminUsers: "/admin/users",
  AdminUser: "/admin/users/:userId",
  Console: "/console",
  ConsoleTenants: "/console/tenants",
  ConsoleTenant: "/console/tenants/:tenantId",
  ConsoleTenantUsers: "/console/tenants/:tenantId/users",
  ConsoleUsers: "/console/users",
  ConsoleUser: "/console/users/:userId",
  AdminFunctions: "/admin/functions",
  AdminControlTags: "/admin/control-tags",
  Agents: "/agents",
  Notifications: "/notifications",
  AdminAppIntegrations: "/admin/app-integrations",
  AdminIntegrations: "/admin/integrations",
  AdminEncryptionKeys: "/admin/encryption-keys",
  AdminInfosecLevels: "/admin/infosec-levels",
  AdminInfosec: "/admin/infosec",
  AdminUsage: "/admin/usage",
  AdminCompatibility: "/admin/compatibility",
  AdminAi: "/admin/ai",
  /** Retired pages, redirected to the tabs of `AdminAi`. */
  AdminAiSettings: "/admin/ai-settings",
  AdminAiModels: "/admin/ai-models",
  AdminSecureConnections: "/admin/secure-connections",
  AdminSecureConnectionSettings: "/admin/secure-connections/:kind/:connectionId",
  AdminSecureConnectionSyncStatus: "/admin/secure-connections/:kind/:connectionId/sync-status",
  CreateChangeRequest: "/systems/:systemId/change-requests/new",
  ChangeRequestDetail: "/systems/:systemId/change-requests/:id",
  /**
   * `/systems/{systemId}/…` — branch path, `…/history`, `…/history/{shortId}`, and legacy
   * `configId/snapshotId/…` sit in a single `*` (see `parseSystemRouteTail` in `System/`).
   */
  SystemDetails: "/systems/:systemId/*",
  Artifact: "/artifact/:artifactId/:artifactVersionId?",
  ResourcesUnparentedArtifact: "/resources/artifact/:artifactId/:artifactRevisionId?",
  /**
   * Connected resources live in their own id space — a `ConnectedResourceDto` exposes no resource
   * id — so they need a segment of their own rather than sharing `/files/:modelId`. The revision
   * segment carries the version being viewed, the same way the resource routes do; without it the
   * versions list has nowhere to put a choice and every row reads as the latest.
   */
  FilesConnectedResource: "/files/connected/:connectedResourceId/:connectedRevisionId?",
  CommentDetails: "/comment/:commentId",
  Search: "/search",
} as const;

export const buildSystemWorkflowLogRoute = (systemId: string, entryId?: string, branchId?: BranchId | null) => {
  const branchPrefix = branchId ? `bid/${encodeURIComponent(branchId)}/` : "";
  return `/systems-dashboard/${systemId}/${branchPrefix}workflows${entryId ? `?entry=${entryId}` : ""}`;
};

export type ResourcePathTarget =
  | { resourceId: string; revisionId?: string }
  | { artifactId: string; artifactRevisionId?: string }
  | { resourceId: string; revisionId?: string; artifactId: string; artifactRevisionId?: string };

export function buildResourcePath(): string;
export function buildResourcePath(target: ResourcePathTarget): string;
export function buildResourcePath(target?: ResourcePathTarget): string {
  if (!target) return routes.Resources;

  if ("artifactId" in target && !("resourceId" in target)) {
    const base = `${routes.Resources}/artifact/${target.artifactId}`;
    return target.artifactRevisionId ? `${base}/${target.artifactRevisionId}` : base;
  }

  if ("resourceId" in target && "artifactId" in target) {
    const parent = target.revisionId ? `${target.resourceId}/${target.revisionId}` : target.resourceId;
    const artifact = target.artifactRevisionId
      ? `${target.artifactId}/${target.artifactRevisionId}`
      : target.artifactId;
    return `${routes.Resources}/${parent}/artifact/${artifact}`;
  }

  return target.revisionId
    ? `${routes.Resources}/${target.resourceId}/${target.revisionId}`
    : `${routes.Resources}/${target.resourceId}`;
}

export type RouteKeys = keyof typeof routes;
export type RouteValues = (typeof routes)[keyof typeof routes];
